import type { NavCounterRow } from '@/data/types'

/**
 * Contadores da barra lateral (SIS-508): o que `supply.nav_counters()` devolve
 * por rota vira um badge.
 *
 *   pendência  algo para a pessoa fazer — número, com a cor da pior
 *              severidade: atrasada (destructive) > vence hoje (warning) >
 *              pendente (primary);
 *   novidade   o que mudou desde a última visita à tela — tom info; sozinha
 *              vira número, junto de pendência vira um ponto.
 *
 * Na rota aberta a novidade não aparece (a pessoa já está vendo).
 */

export type NavSeverity = 'atrasada' | 'hoje' | 'pendente'

export interface NavBadge {
  /** Soma das pendências (0 = sem pendência). */
  readonly pending: number
  readonly severity: NavSeverity | null
  readonly news: number
  /** "3 atrasadas, 1 vence hoje, 2 novidades" — para leitor de tela e tooltip. */
  readonly label: string
}

export type NavCounters = ReadonlyMap<string, NavCounterRow>

export function toCounterMap(rows: readonly NavCounterRow[]): NavCounters {
  return new Map(rows.map((r) => [r.route, r]))
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

export function navBadge(row: NavCounterRow | undefined, active: boolean): NavBadge | null {
  if (!row) return null
  const atrasadas = Math.max(0, row.atrasadas)
  const hoje = Math.max(0, row.hoje)
  const pendentes = Math.max(0, row.pendentes)
  const news = active ? 0 : Math.max(0, row.novidades)
  const pending = atrasadas + hoje + pendentes
  if (!pending && !news) return null
  const parts = [
    atrasadas && plural(atrasadas, 'atrasada', 'atrasadas'),
    hoje && plural(hoje, 'vence hoje', 'vencem hoje'),
    pendentes && plural(pendentes, 'pendente', 'pendentes'),
    news && plural(news, 'novidade', 'novidades'),
  ].filter(Boolean)
  return {
    pending,
    severity: atrasadas ? 'atrasada' : hoje ? 'hoje' : pendentes ? 'pendente' : null,
    news,
    label: parts.join(', '),
  }
}

/** Número no badge: acima de 99 vira "99+". */
export const badgeCount = (n: number) => (n > 99 ? '99+' : String(n))

/** Classes por tom (só tokens do DS). `pill` = número; `dot` = ponto. */
export const NAV_BADGE_TONE: Record<NavSeverity | 'novidade', { readonly pill: string; readonly dot: string }> = {
  atrasada: { pill: 'bg-destructive/15 text-destructive', dot: 'bg-destructive' },
  hoje: { pill: 'bg-warning/15 text-warning', dot: 'bg-warning' },
  pendente: { pill: 'bg-primary/15 text-primary', dot: 'bg-primary' },
  novidade: { pill: 'bg-info/15 text-info', dot: 'bg-info' },
}

/** Rota do menu que contém o caminho (`/quadro?x` → `/quadro`; `/configuracoes/areas` → `/configuracoes`). */
export function navRouteOf(pathname: string, routes: readonly string[]): string | null {
  return routes.find((r) => pathname === r || pathname.startsWith(`${r}/`)) ?? null
}
